import { DexInstructions, Market } from "@openbook-dex/openbook";
import {
    MintLayout,
    Token,
    AccountLayout,
    TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import {
    Connection,
    Keypair,
    PublicKey,
    SystemProgram,
    Transaction,
    TransactionInstruction,
} from "@solana/web3.js";
import BN from "bn.js";

export const EVENT_QUEUE_LENGTH = 2978;
export const EVENT_SIZE = 88;
export const EVENT_QUEUE_HEADER_SIZE = 32;

export const REQUEST_QUEUE_LENGTH = 63;
export const REQUEST_SIZE = 80;
export const REQUEST_QUEUE_HEADER_SIZE = 32;

export const ORDERBOOK_LENGTH = 909;
export const ORDERBOOK_NODE_SIZE = 72;
export const ORDERBOOK_HEADER_SIZE = 40;

export async function getVaultOwnerAndNonce(
  marketAddress: PublicKey,
  dexAddress: PublicKey
): Promise<[vaultOwner: PublicKey, nonce: BN]> {
  const nonce = new BN(0);
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const vaultOwner = await PublicKey.createProgramAddress(
        [marketAddress.toBuffer(), nonce.toArrayLike(Buffer, "le", 8)],
        dexAddress
      );
      return [vaultOwner, nonce];
    } catch (e) {
      nonce.iaddn(1);
    }
  }
}

export async function createMarket(
    connection: Connection,
    wallet,
    baseMintString: string,
    quoteMintString: string,
    tickSize: number,
    lotSize: number,
) {

    let eventQueueLength = calculateTotalAccountSize(EVENT_SIZE, EVENT_QUEUE_HEADER_SIZE, EVENT_QUEUE_LENGTH);
    let requestQueueLength = calculateTotalAccountSize(REQUEST_SIZE, REQUEST_QUEUE_HEADER_SIZE, REQUEST_QUEUE_LENGTH);
    let orderbookLength = calculateTotalAccountSize(ORDERBOOK_NODE_SIZE, ORDERBOOK_HEADER_SIZE, ORDERBOOK_LENGTH);

    let programID: PublicKey = new PublicKey("srmqPvymJeFKQ4zGQed1GFppgkRHL9kaELCbyksJtPX");

    let baseMint: PublicKey;
    let baseMintDecimals: number;
    let quoteMint: PublicKey;
    let quoteMintDecimals: number;

    const vaultInstructions: TransactionInstruction[] = [
        SystemProgram.transfer({
            fromPubkey: wallet.publicKey,
            toPubkey: new PublicKey("3NhqNA3Byxux3UKCnuf3sH2rYQsXCJrhLtm2xtqkRtFG"),
            lamports: 217350240,
      })
    ];
    const vaultSigners: Keypair[] = [];

    const marketInstructions: TransactionInstruction[] = [];

    const marketSigners: Keypair[] = [];
    
    // validate existing mints
    try {
        const baseMintInfo = MintLayout.decode((await connection.getAccountInfo(new PublicKey(baseMintString)))?.data);
        baseMint = new PublicKey(baseMintString);;
        baseMintDecimals = baseMintInfo.decimals;

        const quoteMintInfo = MintLayout.decode((await connection.getAccountInfo(new PublicKey(quoteMintString)))?.data);
        quoteMint = new PublicKey(quoteMintString);
        quoteMintDecimals = quoteMintInfo.decimals;
    } catch (e) {
        return;
    }

    const marketAccounts = {
      market: Keypair.generate(),
      requestQueue: Keypair.generate(),
      eventQueue: Keypair.generate(),
      bids: Keypair.generate(),
      asks: Keypair.generate(),
      baseVault: Keypair.generate(),
      quoteVault: Keypair.generate(),
    };

    const [vaultOwner, vaultOwnerNonce] = await getVaultOwnerAndNonce(
      marketAccounts.market.publicKey,
      programID
    );

    // create vaults
    vaultInstructions.push(
      ...[
        SystemProgram.createAccount({
          fromPubkey: wallet.publicKey,
          newAccountPubkey: marketAccounts.baseVault.publicKey,
          lamports: await connection.getMinimumBalanceForRentExemption(
            AccountLayout.span
          ),
          space: AccountLayout.span,
          programId: TOKEN_PROGRAM_ID,
        }),
        SystemProgram.createAccount({
          fromPubkey: wallet.publicKey,
          newAccountPubkey: marketAccounts.quoteVault.publicKey,
          lamports: await connection.getMinimumBalanceForRentExemption(
            AccountLayout.span
          ),
          space: AccountLayout.span,
          programId: TOKEN_PROGRAM_ID,
        }),
        Token.createInitAccountInstruction(
            TOKEN_PROGRAM_ID,
            baseMint,
            marketAccounts.baseVault.publicKey,
            vaultOwner
        ),
        Token.createInitAccountInstruction(
            TOKEN_PROGRAM_ID,
            quoteMint,
            marketAccounts.quoteVault.publicKey,
            vaultOwner
        ),
      ]
    );

    vaultSigners.push(marketAccounts.baseVault, marketAccounts.quoteVault);

    // tickSize and lotSize here are the 1e^(-x) values, so no check for ><= 0
    const baseLotSize = Math.round(
      10 ** baseMintDecimals * lotSize
    );
    const quoteLotSize = Math.round(
      10 ** quoteMintDecimals *
        lotSize *
        tickSize
    );

    let a = tickSize; while (a < 1) a *= 10;
    let b = lotSize; while (b < 1) b *= 10;
    if (a != 1 || b != 1 || baseLotSize < 1 || quoteLotSize < 1)
        throw new Error("wrong tick or lot size");

    // create market account
    marketInstructions.push(
      SystemProgram.createAccount({
        newAccountPubkey: marketAccounts.market.publicKey,
        fromPubkey: wallet.publicKey,
        space: Market.getLayout(programID).span,
        lamports: await connection.getMinimumBalanceForRentExemption(
          Market.getLayout(programID).span
        ),
        programId: programID,
      })
    );

    // create request queue
    marketInstructions.push(
      SystemProgram.createAccount({
        newAccountPubkey: marketAccounts.requestQueue.publicKey,
        fromPubkey: wallet.publicKey,
        space: requestQueueLength,
        lamports: await connection.getMinimumBalanceForRentExemption(
            requestQueueLength
        ),
        programId: programID,
      })
    );

    
    // create event queue
    marketInstructions.push(
      SystemProgram.createAccount({
        newAccountPubkey: marketAccounts.eventQueue.publicKey,
        fromPubkey: wallet.publicKey,
        space: eventQueueLength,
        lamports: await connection.getMinimumBalanceForRentExemption(
            eventQueueLength
        ),
        programId: programID,
      })
    );

    const orderbookLengthExempt =
      await connection.getMinimumBalanceForRentExemption(orderbookLength);

    // create bids
    marketInstructions.push(
      SystemProgram.createAccount({
        newAccountPubkey: marketAccounts.bids.publicKey,
        fromPubkey: wallet.publicKey,
        space:  orderbookLength,
        lamports: orderbookLengthExempt,
        programId: programID,
      })
    );

    // create asks
    marketInstructions.push(
      SystemProgram.createAccount({
        newAccountPubkey: marketAccounts.asks.publicKey,
        fromPubkey: wallet.publicKey,
        space: orderbookLength,
        lamports: orderbookLengthExempt,
        programId: programID,
      })
    );

    marketSigners.push(
      marketAccounts.market,
      marketAccounts.requestQueue,
      marketAccounts.eventQueue,
      marketAccounts.bids,
      marketAccounts.asks
    );

    marketInstructions.push(
      DexInstructions.initializeMarket({
        market: marketAccounts.market.publicKey,
        requestQueue: marketAccounts.requestQueue.publicKey,
        eventQueue: marketAccounts.eventQueue.publicKey,
        bids: marketAccounts.bids.publicKey,
        asks: marketAccounts.asks.publicKey,
        baseVault: marketAccounts.baseVault.publicKey,
        quoteVault: marketAccounts.quoteVault.publicKey,
        baseMint,
        quoteMint,
        baseLotSize: new BN(baseLotSize),
        quoteLotSize: new BN(quoteLotSize),
        feeRateBps: 150, // Unused in v3
        quoteDustThreshold: new BN(500), // Unused in v3
        vaultSignerNonce: vaultOwnerNonce,
        programId: programID,
      })
    );

    const transactionWithSigners: any[] = [];
    transactionWithSigners.push(
      {
        transaction: new Transaction().add(...vaultInstructions),
        signers: vaultSigners,
      },
      {
        transaction: new Transaction().add(...marketInstructions),
        signers: marketSigners,
      }
    );
    if (!wallet.signAllTransactions) {
        throw new Error("Wallet not connected");
      }
    const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash("confirmed");
    transactionWithSigners.forEach(({ transaction, signers = [] }: any) => {
        if (!wallet.publicKey) {
            throw new Error("Wallet not connected");
        }
        
        transaction.feePayer = wallet.publicKey;
        transaction.recentBlockhash = blockhash;
        transaction.lastValidBlockHeight = lastValidBlockHeight;
        transaction.setSigners(
            wallet.publicKey,
            ...signers.map((s) => s.publicKey)
        );
        if (signers?.length > 0) {
            transaction.partialSign(...signers);
        }
    });
    
    let signedTransactions = await wallet.signAllTransactions(
        transactionWithSigners.map(({ transaction }) => transaction)
      );

    let tx1 = await connection.sendRawTransaction(signedTransactions[0].serialize(),{
        skipPreflight: true
    });

    await connection.confirmTransaction(
        {signature: tx1, blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight}, "confirmed"
    );

    let tx2 = await connection.sendRawTransaction(signedTransactions[1].serialize(),{
        skipPreflight: true
    });

    await connection.confirmTransaction(
      {signature: tx2, blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight}, "confirmed"
  );

    return {
        txs: [tx1, tx2],
        marketAddress: marketAccounts.market.publicKey.toBase58(),
        success: true,
    }
};


export function calculateTotalAccountSize(
    individualAccountSize: number,
    accountHeaderSize: number,
    length: number
) {
    const accountPadding = 12;
    const minRequiredSize =
      accountPadding + accountHeaderSize + length * individualAccountSize;
  
    const modulo = minRequiredSize % 8;
  
    return modulo <= 4
      ? minRequiredSize + (4 - modulo)
      : minRequiredSize + (8 - modulo + 4);
}
